/* TB FINDER — the counter.
 *
 * One input. What comes back:
 *   RESOLVED   — one bike, and TB has a page for it     → trail + open the page
 *   RECOGNISED — one bike, no page yet                  → name it back, capture path
 *   PATHWAY    — a listable set                         → "which one?"
 *   QUIET      — more hits than we'll paint             → NOTHING. See below.
 *   KEYWORD    — "sushi", "sticker", "warranty"         → the shelf or the answer
 *   NO MATCH   — not a bike we hold                     → capture path. Never a dead end.
 *
 * RECOGNISED IS THE MAJORITY EXPERIENCE, NOT THE EDGE CASE.
 * The list holds 2,880 bikes and TB has a page for a fraction of them. Around nine
 * in ten riders who type a bike land here. It exists so the site says their bike's
 * name back to them instead of shrugging. Treat it as the main road.
 *
 * THE QUIET STATE RENDERS NOTHING, DELIBERATELY.
 * Above the threshold there is no panel, no prompt, no "240 KTMs, which model?", no
 * chips. A rider does not need a report on what the search did. One more character
 * takes `300` to 79 to 22 to 5, and they were always going to type it — watching the
 * list arrive IS the interaction. The threshold is a paint ceiling and nothing else;
 * it is never explained to the rider and never named on screen.
 *
 * CRITICAL: this file holds ZERO product data. Bikes come from the index JSON fetched
 * on open; copy, shelves and answers come from the section inline. Data hardcoded in
 * the engine is the exact PS NOT FOUND bug — a stale snapshot the catalogue outgrows.
 *
 * Forgive silently, never patronise. No "did you mean?".
 */
(function () {
  'use strict';

  var bikeIndex = null;    // the index the CURRENT mount is using
  var live = null;         // inline: strings, shelves, answers, common, copy

  // Cached PER URL. This used to be a single shared variable that ignored which file
  // had been asked for, so the first mount to load won and every later mount silently
  // got handed that data instead of its own. With one index file on the site that was
  // invisible; the moment a second one existed it meant a page could serve the old
  // bike list with no error and no clue anything was wrong.
  var cache = {};          // url -> prepared index
  var pending = {};        // url -> in-flight promise

  var DEFAULT_THRESHOLD = 50;

  // How long the no-match message waits before it appears, in milliseconds.
  //
  // Every state paints the instant it's true EXCEPT this one. Typing a year goes
  // through "yz85 2", "yz85 20", "yz85 201" — three keystrokes that legitimately
  // match nothing — before "yz85 2015" lands. Painting each one meant the panel
  // flicked found / not found / not found / not found / found under the rider's
  // hands, which reads as the site being broken rather than as them still typing.
  //
  // So a no-match holds off until the typing stops. Anything found cancels it and
  // paints straight away, so this only ever delays bad news, never an answer. The
  // timer restarts on every keystroke, so it only has to outlast the gap between
  // two keys, not the whole word.
  var NOMATCH_DELAY = 500;
  var ANSWER_CAP = 3;
  var INDICATOR_MIN_CHARS = 2;

  // How much has to be typed before the clear (x) appears. Two characters is still
  // one backspace away, so an x there is clutter offering nothing. Three is the point
  // where wiping it is quicker than deleting it.
  var CLEAR_MIN_CHARS = 3;

  /* ---------- normalisation ---------- */

  function norm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Segments split on punctuation AND on letter/digit transitions:
  //   "R75/5"     -> r · 75 · 5
  //   "GSX-R750"  -> gsx · r · 750
  //   "250 SX-F"  -> 250 · sx · f
  // That boundary is the whole trick. Matching anywhere in the string made "r75"
  // return VFR750R, XR75, NSR75 and Monster 750 alongside the actual BMWs.
  function segments(s) {
    var out = [];
    String(s || '').split(/[^A-Za-z0-9]+/).forEach(function (part) {
      if (!part) return;
      (part.match(/[A-Za-z]+|[0-9]+/g) || []).forEach(function (x) {
        out.push(x.toLowerCase());
      });
    });
    return out;
  }

  // Every suffix of the segment list, joined. "250 SX-F" -> 250sxf · sxf · f
  // A term matches when it's a PREFIX of one of these — i.e. it starts at a real
  // word boundary inside the name.
  function anchorsOf(s) {
    var w = segments(s);
    var a = [];
    for (var i = 0; i < w.length; i++) a.push(w.slice(i).join(''));
    return a;
  }

  // Words riders type that carry no signal. "the 85" must find the YZ85/KX85.
  var STOPWORDS = ['the', 'a', 'an', 'my', 'me', 'for', 'on', 'to', 'it', 'i', 'ive',
                   'got', 'have', 'has', 'ride', 'riding', 'rides', 'bike', 'fits', 'fit', 'and'];

  function tokenize(q) {
    return String(q || '')
      .split(/\s+/)
      .map(norm)
      .filter(function (t) { return t && STOPWORDS.indexOf(t) === -1; });
  }

  function isYear(t) {
    return /^(19|20)\d{2}$/.test(t);
  }

  /* ---------- keyboard ---------- */

  function isTouch() {
    return !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
  }

  // Enter drops the keyboard. Always, every state, no exceptions.
  //
  // iOS will happily re-focus a field blurred from inside its own keydown handler,
  // which is why a plain blur() sometimes left the keyboard sitting there — the rider
  // hits go, nothing visible happens, and it reads as broken. A second pass on the
  // next frame is what actually drops it.
  function dismissKeyboard(field) {
    if (!field) return;
    field.blur();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () {
        if (document.activeElement === field && field.blur) field.blur();
      });
    }
  }

  // For the ask-form fields only: on a real keyboard, Enter-to-submit is correct and
  // expected, so we only intercept on touch. TEXTAREA is exempt — Enter is a newline
  // in a message box. Not negotiable.
  function enterDismisses(field, touchOnly) {
    if (!field || field.tagName === 'TEXTAREA') return;
    field.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      if (touchOnly && !isTouch()) return;
      e.preventDefault();
      dismissKeyboard(field);
    });
  }

  /* ---------- data ---------- */

  function prepareBikes(data) {
    (data.bikes || []).forEach(function (b, i) {
      var modelAnchors = anchorsOf(b.model);
      var anchors = modelAnchors.slice();
      (b.aliases || []).forEach(function (a) {
        anchors = anchors.concat(anchorsOf(a));
      });
      b._i = i;
      // make + model as one string, so a make query prefix-matches the whole row
      b._full = norm(b.make) + (modelAnchors[0] || '');
      b._anchors = anchors;
      // Rank-3 haystack. A fragment that only lands mid-name is still SHOWN, it just
      // sorts last. Surfacing beats whittling.
      b._sub = [b._full].concat((b.aliases || []).map(norm));
      if (!b.years) b.years = b.from === b.to ? String(b.from) : b.from + '\u2013' + b.to;
    });
    return data;
  }

  // Words that carry no intent. They are stripped from what the RIDER types, not
  // from the keys, because a two-letter fragment prefix-matches half the shelf:
  // "do" hits "donut", "how" hits "how long". Ask a polite question and the old
  // matcher answered with the donuts aisle.
  var STOP = ("a an and any are as at be but can did do does for from get got has "
    + "have how i if in is it its me my not of on or our so that the their there "
    + "they this to us was we what when where which who why will with you your"
    ).split(" ").reduce(function (o, w) { o[w] = 1; return o; }, {});

  // One key becomes several: the whole phrase as a single token, plus every word
  // in it worth three letters. Without this a multi-word keyword collapses to one
  // run-together token — "how long" only ever matched someone typing "howlong",
  // "grip donut" only "gripdonut". Every multi-word keyword on the site was dead.
  function keyify(str) {
    var out = [], whole = norm(str);
    if (whole) out.push(whole);
    String(str).toLowerCase().split(/[^a-z0-9]+/).forEach(function (w) {
      if (w.length >= 3 && !STOP[w] && w !== whole) out.push(w);
    });
    return out;
  }

  function keysFrom(list, alsoText) {
    var out = [];
    (list || []).forEach(function (k) { out = out.concat(keyify(k)); });
    // An answer is findable by its own question. Otherwise every obvious word has
    // to be hand-listed as a keyword, and the one titled "What if it doesn't fit?"
    // could not be found by typing "fit".
    if (alsoText) {
      String(alsoText).toLowerCase().split(/[^a-z0-9]+/).forEach(function (w) {
        if (w.length >= 3 && !STOP[w]) out.push(w);
      });
    }
    var seen = {}, uniq = [];
    out.forEach(function (k) { if (k && !seen[k]) { seen[k] = 1; uniq.push(k); } });
    return uniq;
  }

  // Every make in the index, normalised. Used to tell a bike word from an
  // ordinary one when deciding whether to retry a failed match.
  var MAKES = {};
  function prepareMakes(index) {
    ((index && index.bikes) || []).forEach(function (b) { MAKES[norm(b.make)] = 1; });
  }

  function prepareLive(data) {
    (data.shelves || []).forEach(function (s) {
      s._keys = keysFrom(s.keywords, s.label);
      s._qkeys = keysFrom(null, s.label);
    });
    (data.answers || []).forEach(function (a) {
      a._keys = keysFrom(a.keywords, a.question);
      // The words of the question itself. An answer whose SUBJECT is the word the
      // rider typed should beat one that merely lists it as an adjacent keyword:
      // "cam" is the subject of "What's a throttle cam?" and a keyword on
      // "What if it doesn't fit?" (via "wrong cam"). Same score, different answer.
      a._qkeys = keysFrom(null, a.question);
    });
    return data;
  }

  // The live half — copy, shelves, answers, part labels — is rendered inline by the
  // section so it goes live the moment it's saved in the theme editor. If the section
  // hasn't been rebuilt yet there's no inline block, so we fall back to reading it out
  // of the fetched index (the old single-file shape). That fallback is what lets this
  // engine ship on its own, ahead of the header and the new index, without breaking
  // anything.
  function readLive(root) {
    var node = root.querySelector('[data-tb-finder-live]');
    if (!node) return null;
    try {
      return prepareLive(JSON.parse(node.textContent));
    } catch (e) {
      if (window.console) console.error('[TB Finder] live block', e);
      return null;
    }
  }

  function load(url) {
    if (cache[url]) { bikeIndex = cache[url]; prepareMakes(bikeIndex); return Promise.resolve(bikeIndex); }
    if (pending[url]) return pending[url];
    pending[url] = fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('index ' + r.status);
        return r.json();
      })
      .then(function (data) {
        cache[url] = prepareBikes(data);
        bikeIndex = cache[url];
        prepareMakes(bikeIndex);
        // Old-shape index and no inline block: the copy is in here.
        if (!live && (data.strings || data.shelves)) live = prepareLive(data);
        return bikeIndex;
      })
      .catch(function (e) {
        delete pending[url];   // a failed load must be retryable, not stuck
        throw e;
      });
    return pending[url];
  }

  // Diagnostic hook. Type TBFinder.report() in the browser console to see exactly
  // which file each Finder on the page is pointed at and how many bikes it holds.
  // Answers "is it reading the right index?" without opening the network tab.
  window.TBFinder = window.TBFinder || {};
  window.TBFinder.report = function () {
    var rows = [];
    document.querySelectorAll('[data-tb-index]').forEach(function (root) {
      var url = root.getAttribute('data-tb-index');
      rows.push({
        mount: root.tagName.toLowerCase() + (root.id ? '#' + root.id : ''),
        file: String(url).split('/').pop().split('?')[0],
        bikes: cache[url] ? cache[url].bikes.length : 'not loaded yet',
        liveBlock: !!root.querySelector('[data-tb-finder-live]'),
        threshold: root.getAttribute('data-tb-threshold') || '(default ' + DEFAULT_THRESHOLD + ')'
      });
    });
    if (console.table) console.table(rows); else console.log(rows);
    return rows;
  };

  /* ---------- resolve ---------- */

  // PREFIX match for keywords, not substring-anywhere. "modify" contains "odi" and
  // "guarantee" contains "tee", and both used to open the wrong shelf.
  function hits(keys, terms) {
    return terms.some(function (t) {
      if (t.length < 2) return false;
      return keys.some(function (k) {
        return k.indexOf(t) === 0 || t.indexOf(k) === 0;
      });
    });
  }

  // Per-term match quality against one bike:
  //   0  the term IS a segment run        ("85" on KX85, "sx65" on the alias)
  //   1  prefix of the name's own start   ("crf110" on CRF1100L…, "honda" on the make)
  //   2  prefix of a later segment        ("r75" on GSX-R750 -> r750)
  //   3  lands mid-name and nowhere else  ("sx65" inside gsx650f)
  // `extra` is how much longer the match is than the term — the closeness.
  //
  // Rank 3 is never a reason to DROP a bike. What stops it being a confidently wrong
  // answer is that the right bike outranks it: `sx65` is an alias on the 65 SX, so the
  // KTMs sort above the GSX650F instead of the Suzuki being the only thing on screen.
  function score(b, t) {
    var best = -1, extra = 0, i;

    function consider(a, rank) {
      if (a.indexOf(t) !== 0) return;
      var r = a === t ? 0 : rank;
      var e = a.length - t.length;
      if (best === -1 || r < best || (r === best && e < extra)) { best = r; extra = e; }
    }

    consider(b._full, 1);
    for (i = 0; i < b._anchors.length; i++) consider(b._anchors[i], i === 0 ? 1 : 2);

    if (best === -1) {
      for (i = 0; i < b._sub.length; i++) {
        var h = b._sub[i];
        if (h.indexOf(t) === -1) continue;
        var e2 = h.length - t.length;
        if (best === -1 || e2 < extra) { best = 3; extra = e2; }
      }
    }
    return best === -1 ? null : { rank: best, extra: extra };
  }

  // Match terms against the list, optionally gated to a set of years.
  // Returns the bikes ranked: exact -> prefix-at-start -> prefix-deeper -> mid-name,
  // then closest match, then the order they sit in the file.
  //
  // THERE IS NO CODE DOOR, DELIBERATELY. It used to collapse the result to a single
  // row whenever exactly one bike matched outright, binning every partial — that's
  // what hid all three BMW S1000s behind the one Suzuki whose name happens to end at
  // `s1000`. Ranking already puts an outright match on top, which was the only thing
  // the door was for. Keep the ordering, lose the exclusion.
  function match(terms, years) {
    if (!terms.length || !bikeIndex) return [];
    var scored = [];
    bikeIndex.bikes.forEach(function (b) {
      for (var y = 0; y < years.length; y++) {
        if (years[y] < b.from || years[y] > b.to) return;
      }
      var rank = 0, extra = 0, ok = true;
      for (var i = 0; i < terms.length; i++) {
        var s = score(b, terms[i]);
        if (!s) { ok = false; break; }
        if (s.rank > rank) rank = s.rank;
        extra += s.extra;
      }
      if (ok) scored.push({ b: b, rank: rank, extra: extra });
    });
    scored.sort(function (x, y2) {
      return x.rank - y2.rank || x.extra - y2.extra || x.b._i - y2.b._i;
    });
    return scored.map(function (s) { return s.b; });
  }

  // Riders write years short: "yz85 24", "crf110 '19". We can't read a bare 2-digit
  // number as a year up front, because in this industry two digits is usually the
  // MODEL — "yz 85", "kx 65", "the 85". So it's only ever tried as a last resort,
  // after a normal read has come back with nothing. A query that already works can
  // never be changed by this.
  function twoDigitYear(t) {
    if (!/^\d{2}$/.test(t)) return null;
    var n = Number(t);
    var now = new Date().getFullYear() % 100;
    return n <= now + 1 ? 2000 + n : 1900 + n;
  }

  function resolve(q, threshold) {
    if (!bikeIndex && !live) return { state: 'idle', chars: 0, count: 0, terms: [] };

    var toks = tokenize(q);
    if (!toks.length) return { state: 'idle', chars: 0, count: 0, terms: [] };

    var years = toks.filter(isYear).map(Number);
    var terms = toks.filter(function (t) { return !isYear(t); });
    var chars = terms.join('').length;

    var bikes = match(terms, years);

    // "yz65 cam" must land on the YZ65, not on three FAQ answers about cams.
    //
    // The matcher needs every word to hit a bike, so one product word kills the
    // whole query. When nothing matched, keep only the words that match a bike on
    // their own and try again. Generic on purpose — no list of words to ignore, so
    // "yz65 donuts", "crf110 grips" and anything else invented later work the same
    // way without maintenance.
    //
    // The bike page carries every option anyway: if TB makes a cam it is the first
    // one there, and if not, the grip maker covers it. One page, all the answers.
    // Guarded: only when a kept word actually NAMES a bike — it carries a digit
    // like "yz65" or "crf110", or it is a make. Without that guard the fallback
    // fires on any query where one ordinary word happens to graze the index:
    // "out of stock" lost its answer because "stock" matched something, and a
    // half-typed model was dragged to a result instead of waiting to say no.
    if (!bikes.length && terms.length > 1) {
      var kept = terms.filter(function (t) { return match([t], []).length > 0; });
      var dropped = terms.filter(function (t) { return kept.indexOf(t) === -1; });
      // Never drop a number. "cam" is a product word and dropping it changes
      // nothing; "201" is a half-typed year and dropping it would resolve the bike
      // as though the year were served. A partial left alone has to say no.
      var safe = dropped.length && dropped.every(function (t) { return !/^[0-9]+$/.test(t); });
      // And something kept has to actually NAME a bike — a make, or a model-shaped
      // word with letters and digits in it. Otherwise "out of stock" retries on
      // whichever ordinary word happens to graze the index and loses its answer.
      var named = kept.some(function (t) {
        return MAKES[t] || (/[a-z]/.test(t) && /[0-9]/.test(t));
      });
      if (safe && named) {
        var retry = match(kept, years);
        if (retry.length) { bikes = retry; terms = kept; }
      }
    }
    var yearMissed = false;

    // A year we don't hold must not dead-end the bike.
    // "drz400 1999" used to come back "we don't have that one in the list" — but the
    // DR-Z400 is right there, it just starts in 2000. Telling a rider their bike
    // doesn't exist because they were a year out is the worst answer available.
    // So: keep the bikes the words matched, and show their real years.
    if (!bikes.length && years.length && terms.length) {
      var ungated = match(terms, []);
      if (ungated.length) { bikes = ungated; yearMissed = true; }
    }

    // Last resort: re-read a bare 2-digit token as a year.
    if (!bikes.length && terms.length > 1) {
      for (var t = 0; t < terms.length; t++) {
        var yr = twoDigitYear(terms[t]);
        if (!yr) continue;
        var rest = terms.slice(0, t).concat(terms.slice(t + 1));
        var retry = match(rest, years.concat([yr]));
        if (retry.length) { bikes = retry; terms = rest; years = years.concat([yr]); break; }
        var loose = match(rest, years);
        if (loose.length) { bikes = loose; terms = rest; yearMissed = true; break; }
      }
    }

    // "yz85 201" — a year half typed. The old answer was "we don't have that one in
    // the list", which is flatly untrue: the YZ85 is right there. Show the bike and
    // ask for the year instead. A partial year is an unfinished question, not a miss.
    var partialYear = false;
    if (!bikes.length && terms.length > 1) {
      // What a year looks like half typed: 1 or 2, then 19 or 20, then 199/201/202.
      // Deliberately NOT any three-digit number — 250, 125, 450 and 300 are models,
      // not years, and treating them as years would strip the bike out of the query.
      var looksLikeYear = function (t) {
        return !isYear(t) && (/^[12]$/.test(t) || /^(19|20)[0-9]?$/.test(t));
      };
      // Work backwards. A rider types the year LAST, so on "ktm 200 201" the
      // fragment is 201 and 200 is the bike — taking the first candidate would
      // throw away the model and keep the typo.
      for (var f = terms.length - 1; f >= 0; f--) {
        if (!looksLikeYear(terms[f])) continue;
        var rest2 = terms.slice(0, f).concat(terms.slice(f + 1));
        if (!rest2.length) continue;
        var got = match(rest2, years);
        if (got.length) { bikes = got; terms = rest2; partialYear = true; break; }
      }
    }

    var count = bikes.length;
    // Strip the filler before matching. "how do i contact you" should ask about
    // contact, not about the two keys that happen to start with "how" and "do".
    var kterms = terms.filter(function (t) { return t.length >= 3 && !STOP[t]; });
    if (!kterms.length) kterms = terms;
    // Filtered, then RANKED. Block order is not relevance: "throttle sticking" used
    // to return "Where's it made?" and "Can I stock ThrottleBoss?" while the answer
    // about a throttle that won't snap back — a safety answer — got sliced off the
    // bottom. More answers made that worse, not better.
    //
    //   3  the key IS the word         "sticking" on "sticking"
    //   2  the word starts a key       "track" on "tracking"
    //   1  the key starts the word     "cam" matching inside a longer typed word
    //
    // Summed across every term the rider typed, so an answer that matches two of
    // their words beats one that grazes a single word.
    function score(keys, qkeys, ts) {
      var n = 0;
      ts.forEach(function (t) {
        var best = 0;
        keys.forEach(function (k) {
          if (k === t) best = Math.max(best, 3);
          else if (k.indexOf(t) === 0) best = Math.max(best, 2);
          else if (t.indexOf(k) === 0) best = Math.max(best, 1);
        });
        // half a point for being what the answer is actually ABOUT. Small on
        // purpose: enough to break a tie, never enough to beat a real two-word
        // match. "wrong cam" still resolves to returns, "what cam" no longer does.
        if (best && (qkeys || []).some(function (k) { return k === t || k.indexOf(t) === 0; })) best += 0.5;
        n += best;
      });
      return n;
    }
    function ranked(list, ts) {
      return list.map(function (x, i) { return { x: x, s: score(x._keys, x._qkeys, ts), i: i }; })
                 .filter(function (r) { return r.s > 0; })
                 // block order breaks ties, so a deliberate ordering still counts
                 .sort(function (a, b) { return b.s - a.s || a.i - b.i; })
                 .map(function (r) { return r.x; });
    }
    var kAnswers = ranked((live && live.answers) || [], kterms);
    var kShelves = ranked((live && live.shelves) || [], kterms);

    var base = { years: years, terms: terms, chars: terms.join('').length,
                 count: count, yearMissed: yearMissed,
                 // Answers ride ALONGSIDE bike results rather than losing to them.
                 // "contact" is a real GASGAS trials bike, so the bike matcher won
                 // outright and the how-to-reach-us answer never showed. Both are
                 // legitimate readings of the word; show both and let the rider pick.
                 partialYear: partialYear,
                 alsoAnswers: kAnswers.slice(0, ANSWER_CAP) };

    // When the year didn't land, never resolve straight through and never echo the
    // year back — both would claim a fit for a year TB doesn't hold. Show the row
    // instead: the rider reads the real years off it and decides.
    if (yearMissed) {
      if (count > threshold) return extend(base, { state: 'quiet', bikes: bikes });
      return extend(base, { state: 'pathway', bikes: bikes });
    }

    if (count === 1) {
      var b0 = bikes[0];
      // A page exists, but not for that year — name the bike, don't claim the fit.
      var served = b0.url && servesYear(b0, years);
      return extend(base, { state: served ? 'resolved' : 'recognised', bike: b0 });
    }
    if (count > threshold) return extend(base, { state: 'quiet', bikes: bikes });
    if (count > 1) return extend(base, { state: 'pathway', bikes: bikes });

    // KEYWORD door — answers AND shelves together. No precedence fight: "rare"
    // legitimately means both the Drops aisle and the drops-don't-disappear answer.
    // Strip the filler before matching. "how do i contact you" should ask about
    // contact, not about the two keys that happen to start with "how" and "do".
    var answers = kAnswers, shelves = kShelves;
    if (answers.length || shelves.length) {
      return extend(base, {
        state: 'keyword',
        answers: answers.slice(0, ANSWER_CAP),
        shelves: shelves
      });
    }

    return extend(base, { state: 'nomatch' });
  }

  function extend(a, b) {
    Object.keys(b).forEach(function (k) { a[k] = b[k]; });
    return a;
  }

  // Does TB serve this bike in the year they typed?
  //
  // A bike's from/to is how long the MODEL existed. ff/ft, when present, is the
  // narrower slice TB can actually supply — 62 of the 131 pages are narrower, and
  // some by decades: the YZ125 has been made since 1974, the grips are stated from
  // 1996. Matching on the model's whole life is what lets the Finder recognise a
  // 1980 YZ125 at all. This is what stops it then sending him to a page that says
  // it fits.
  function servesYear(b, years) {
    var i;
    // A year punched out of the middle of the range. The 2026 65 SX went to micro
    // bars for one year and back again, so it sits inside a fitment that otherwise
    // runs straight through. Cheaper than splitting the bike into two pages, and it
    // means a 2026 owner gets named rather than sold a cam that won't take his grips.
    if (b.ex && b.ex.length) {
      for (i = 0; i < years.length; i++) {
        if (b.ex.indexOf(years[i]) !== -1) return false;
      }
    }
    if (!b.ff && !b.ft) return true;
    var lo = b.ff || b.from, hi = b.ft || b.to;
    for (i = 0; i < years.length; i++) {
      if (years[i] < lo || years[i] > hi) return false;
    }
    return true;
  }

  // THE ECHO. Typed a year, echo THEIR year. Typed none, echo the held range.
  // Never infer a year, never default to the current one. The range proves TB knows
  // the bike; the year proves TB was listening. Two years typed isn't a year — fall
  // back to the range rather than picking one.
  function echoYears(bike, years) {
    return (years && years.length === 1) ? String(years[0]) : bike.years;
  }

  function bikeName(bike, years) {
    return bike.make + ' ' + bike.model + ' ' + echoYears(bike, years);
  }

  /* ---------- render ---------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Wording lives in the data, never in here. {tokens} interpolate.
  function str(key, vars) {
    var s = (live && live.strings && live.strings[key]) || '';
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.split('{' + k + '}').join(vars[k]);
      });
    }
    return s;
  }

  // Copy blocks: recognised falls back to nomatch, so an index that predates the
  // recognised state still renders something true rather than nothing.
  function copyBlock(key) {
    return (live && live[key]) || (live && live.nomatch) || {};
  }

  function interp(s, vars) {
    s = String(s || '');
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.split('{' + k + '}').join(vars[k]);
      });
    }
    return s;
  }

  // A bike's parts through the registry. The engine has no idea what a "cam" is — it
  // renders whatever types the data declares. Add a part category and this handles it.
  function partsOf(bike) {
    return (bike.parts || []).map(function (code) {
      var p = (live && live.parts && live.parts[code]) || {};
      var t = (live && live.part_types && live.part_types[p.type]) || {};
      return { code: code, type: p.type, label: p.label || code, url: p.url,
               role: t.role, typeLabel: t.label };
    });
  }

  function primaryPart(bike) {
    var all = partsOf(bike);
    for (var i = 0; i < all.length; i++) if (all[i].role === 'primary') return all[i];
    return all[0] || null;
  }

  function label(text) {
    return el('p', 'tb-finder__label', text);
  }

  // PERMANENT CHROME — built once, never touched by paint().
  // The aisle row and the common questions stay put while the rider types; only the
  // results area above them changes. Living outside `out` is what makes it permanent:
  // paint() clears `out` on every keystroke, which would also collapse an expanded
  // answer mid-read.
  function buildPermanent(host) {
    host.appendChild(el('p', 'tb-finder__label tb-finder__goods', str('idle_label')));
    var chips = el('div', 'tb-finder__chips');
    ((live && live.shelves) || []).forEach(function (s) {
      var a = el('a', 'tb-finder__chip', s.name);
      a.href = s.url;
      chips.appendChild(a);
    });
    host.appendChild(chips);

    var byId = {};
    ((live && live.answers) || []).forEach(function (a) { byId[a.id] = a; });
    var picks = ((live && live.common) || []).map(function (id) { return byId[id]; })
                                             .filter(Boolean);
    if (!picks.length) return;

    host.appendChild(el('p', 'tb-finder__label tb-finder__common-label', str('common_label')));
    picks.forEach(function (a) { host.appendChild(commonRow(a)); });
  }

  // The commonly-asked accordion row. Lifted out of the permanent block so the
  // no-match state can reuse it: someone typing "tracking" is not looking for a
  // bike, and the no-match copy talks about nothing but bikes.
  function commonRow(a) {
    var item = el('div', 'tb-finder__q');
    var btn = el('button', 'tb-finder__q-ask', a.question || a.answer);
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    var body = el('div', 'tb-finder__q-body');
    body.hidden = true;
    body.appendChild(el('p', 'tb-finder__answer-text', a.answer));
    if (a.link_label && a.link_url) {
      var link = el('a', 'tb-finder__answer-link', a.link_label);
      link.href = a.link_url;
      body.appendChild(link);
    }
    btn.addEventListener('click', function () {
      var open = !body.hidden;
      body.hidden = open;
      btn.setAttribute('aria-expanded', String(!open));
      item.classList.toggle('is-open', !open);
    });
    item.appendChild(btn);
    item.appendChild(body);
    return item;
  }

  // A row for a bike with a page navigates. A row for a bike WITHOUT one resolves in
  // place, into the recognised state — it does not navigate and it is not inert.
  // Previously a url-less row rendered a button whose click handler did nothing at
  // all, which with 2,749 such bikes was a lot of dead buttons.
  function bikeRow(b, onPick) {
    var row = el('button', 'tb-finder__row');
    row.type = 'button';
    var name = el('span', 'tb-finder__row-name');
    name.appendChild(el('span', 'tb-finder__row-make', b.make));
    name.appendChild(document.createTextNode(' '));
    name.appendChild(el('span', 'tb-finder__row-model', b.model));
    row.appendChild(name);
    row.appendChild(el('span', 'tb-finder__row-years', b.years));
    row.addEventListener('click', function () {
      if (b.url) { window.location.href = b.url; return; }
      if (onPick) onPick(b);
    });
    return row;
  }

  function renderResolved(out, b, years) {
    var trail = el('p', 'tb-finder__trail');
    trail.appendChild(el('span', 'tb-finder__trail-bike', b.make + ' ' + b.model));

    var p = primaryPart(b);
    if (p) {
      trail.appendChild(document.createTextNode(' \u2192 ' + str('trail_fits') + ' \u2192 '));
      trail.appendChild(el('span', 'tb-finder__trail-cam', p.code));
    }
    trail.appendChild(document.createTextNode(' \u2192 ' + str('trail_end')));
    out.appendChild(trail);

    var extras = partsOf(b).filter(function (x) { return x.role !== 'primary'; });
    if (extras.length) {
      var ul = el('div', 'tb-finder__extras');
      extras.forEach(function (x) {
        ul.appendChild(el('span', 'tb-finder__extra', x.typeLabel || x.label));
      });
      out.appendChild(ul);
    }

    var cta = el('a', 'tb-finder__cta', str('cta_open', { model: b.model }));
    cta.href = b.url;
    out.appendChild(cta);
  }

  // RECOGNISED — TB knows the bike and has nothing for it yet.
  // The bike's name goes INSIDE the heading rather than in a row of its own, so this
  // needs no markup the no-match state doesn't already have. Invites, never forces:
  // the ask button is a button, and the shelves and questions stay visible below it.
  function renderRecognised(out, b, years) {
    var c = copyBlock('recognised');
    var name = bikeName(b, years);
    out.appendChild(el('p', 'tb-finder__nomatch-head', interp(c.heading, { bike: name })));
    if (c.body) out.appendChild(el('p', 'tb-finder__nomatch-body', interp(c.body, { bike: name })));
    if (c.foot) out.appendChild(el('p', 'tb-finder__nomatch-foot', c.foot));
  }

  function renderPathway(out, bikes, onPick, yearMissed, years) {
    if (yearMissed) {
      // Say it. Falling back to the list without a word looks like the year was
      // accepted and quietly ignored — the rider has no idea whether we read it, or
      // whether they fat-fingered it. Typing 2028 when nothing goes past 2026 is
      // nearly always a typo, and it's cheap to ask.
      var line = str('year_miss', { year: years && years.length ? years[0] : '' });
      if (line) out.appendChild(el('p', 'tb-finder__nomatch-body', line));
    } else if (bikes.length > 1) {
      // One row needs no heading over it — "1 BIKES — WHICH ONE?" reads like a fault.
      // The row already says the make, the model and the years.
      out.appendChild(label(str('pathway_label', { n: bikes.length })));
    }
    bikes.forEach(function (b) { out.appendChild(bikeRow(b, onPick)); });
  }

  function renderShelves(out, shelves) {
    out.appendChild(label(str('shelves_label')));
    shelves.forEach(function (s) {
      var a = el('a', 'tb-finder__row');
      a.href = s.url;
      var name = el('span', 'tb-finder__row-name');
      name.appendChild(el('span', 'tb-finder__row-model', s.name));
      a.appendChild(name);
      a.appendChild(el('span', 'tb-finder__row-years', s.blurb));
      out.appendChild(a);
    });
  }

  function renderAnswers(out, answers) {
    out.appendChild(label(str('answers_label')));
    answers.forEach(function (a) {
      var card = el('div', 'tb-finder__answer');
      if (a.question) card.appendChild(el('p', 'tb-finder__answer-q', a.question));
      card.appendChild(el('p', 'tb-finder__answer-text', a.answer));
      if (a.link_label && a.link_url) {
        var link = el('a', 'tb-finder__answer-link', a.link_label);
        link.href = a.link_url;
        card.appendChild(link);
      }
      out.appendChild(card);
    });
  }

  function renderNoMatch(out) {
    var n = (live && live.nomatch) || {};
    if (n.heading) out.appendChild(el('p', 'tb-finder__nomatch-head', n.heading));
    if (n.body) out.appendChild(el('p', 'tb-finder__nomatch-body', n.body));
    if (n.foot) out.appendChild(el('p', 'tb-finder__nomatch-foot', n.foot));

    // Someone typing "tracking" or "install" is not looking for a bike, and the
    // no-match copy is written entirely about bikes: "we don't have that one in the
    // list". Put the commonly-asked questions underneath so a question-shaped miss
    // lands on questions instead of a dead end. Costs nothing when it isn't wanted
    // — the same block already sits under the idle state.
    var common = ((live && live.common) || []).map(function (id) {
      return ((live && live.answers) || []).filter(function (a) { return a.id === id; })[0];
    }).filter(Boolean);
    if (common.length) {
      out.appendChild(el('p', 'tb-finder__label tb-finder__common-label', str('common_label')));
      common.forEach(function (a) { out.appendChild(commonRow(a)); });
    }
  }

  /* ---------- wire ---------- */

  function init(root) {
    var input = root.querySelector('[data-tb-visor-input]');
    var out = root.querySelector('[data-tb-visor-results]');
    var url = root.getAttribute('data-tb-index');
    if (!input || !out || !url) return;

    // The quiet indicator. Optional: a section built without it still resolves.
    var indicator = root.querySelector('[data-tb-visor-indicator]');
    var clear = root.querySelector('[data-tb-visor-clear]');

    var threshold = parseInt(root.getAttribute('data-tb-threshold'), 10);
    if (!threshold || threshold < 1) threshold = DEFAULT_THRESHOLD;

    var inline = root.hasAttribute('data-tb-inline');

    // Copy and shelves are in the DOM already, so the permanent chrome can paint
    // before the bike index has finished downloading.
    if (!live) live = readLive(root);

    var ask = root.querySelector('[data-tb-ask]');
    var askOpen = root.querySelector('[data-tb-ask-open]');
    var askWrap = root.querySelector('[data-tb-ask-form]');
    var askForm = askWrap ? askWrap.querySelector('form') : null;
    var askBike = root.querySelector('[data-tb-ask-bike]');
    var askOk = root.querySelector('[data-tb-ask-ok]');
    var askErr = root.querySelector('[data-tb-ask-err]');
    var askSubmit = root.querySelector('[data-tb-ask-submit]');
    var askPage = root.querySelector('[data-tb-ask-page]');
    var askItem = root.querySelector('[data-tb-ask-item]');
    var capNote = root.querySelector('[data-tb-ask-note]');
    var msgToggle = root.querySelector('[data-tb-msg-toggle]');
    var msg = root.querySelector('[data-tb-msg]');

    var perm = null;
    function ensurePermanent() {
      if (inline || perm || !live) return;
      perm = el('div', 'tb-finder__perm');
      buildPermanent(perm);
      var anchor = ask || out;
      if (anchor.parentNode) anchor.parentNode.insertBefore(perm, anchor.nextSibling);
    }

    if (askWrap) {
      askWrap.querySelectorAll('input').forEach(function (f) { enterDismisses(f, true); });
    }

    var picked = null;      // a row tapped out of a pathway list
    var lastName = '';      // what the ask form should carry
    var captured = false;   // opened by a Message-to-secure, not by a search

    function resetAsk() {
      captured = false;
      if (askPage) askPage.value = '';
      if (askItem) askItem.value = '';
      if (askBike) askBike.value = '';
      if (capNote) { capNote.hidden = true; capNote.textContent = ''; }
      if (!ask) return;
      ask.hidden = true;
      if (askOpen) askOpen.hidden = false;
      if (askWrap) askWrap.hidden = true;
      if (askOk) askOk.hidden = true;
      if (askErr) askErr.hidden = true;
    }

    function openAsk() {
      if (!ask || ask.hidden || !askOpen || askOpen.hidden) return false;
      askOpen.click();
      return true;
    }

    // Current view: a tapped row wins over whatever the field currently resolves to.
    function view() {
      var r = resolve(input.value, threshold);
      if (picked) {
        return extend(r, {
          state: picked.url ? 'resolved' : 'recognised',
          bike: picked,
          years: []
        });
      }
      return r;
    }

    var noMatchTimer = null;
    var showingNoMatch = false;

    // ── SEARCH LOGGING ──────────────────────────────────────────────────────
    //
    // The ask form only catches riders who cared enough to type an email. This
    // catches everyone, and it answers the two questions TB can't answer any
    // other way:
    //
    //   typed, and came back with nothing to sell   -> the cam roadmap, by demand
    //   typed, and isn't in the list at all         -> a hole in the index
    //
    // Fires ONCE per settled query, never per keystroke: "k / kx / kx4 / kx45" is
    // one row, not four. Anonymous — the term and the outcome, nothing else. No
    // id, no session, nothing joinable to a person.
    var logTimer = null, lastLogged = '';
    var LOG_DELAY = 700;

    function bucket(r) {
      if (r.state === 'resolved')   return (r.bike && r.bike.parts) ? 'cam' : 'grips';
      if (r.state === 'recognised') return 'recognised';
      if (r.state === 'nomatch')    return 'no_match';
      if (r.state === 'keyword')    return 'keyword';
      return 'multi';               // pathway + quiet: still narrowing, not an answer
    }

    function resetLog() {
      if (logTimer) { clearTimeout(logTimer); logTimer = null; }
      lastLogged = '';
    }

    function logQuery(r) {
      var q = input.value.trim().toLowerCase();
      if (!q || q.length < 2) return;
      if (r.state === 'idle') return;
      if (q === lastLogged) return;
      lastLogged = q;
      var b = bucket(r);

      // Shopify's own analytics — no third party, no extra script, no cookie.
      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
          Shopify.analytics.publish('search_submitted', {
            searchResult: { query: q, productVariants: [] },
            customData: { tb_outcome: b, tb_count: r.count || 0 }
          });
        }
      } catch (e) {}

      // And a dataLayer push, so GA4 or anything else picks it up the day it is
      // added rather than needing this file reopened.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'tb_finder_search', tb_query: q,
          tb_outcome: b, tb_count: r.count || 0
        });
      } catch (e) {}
    }

    function scheduleLog(r) {
      if (logTimer) clearTimeout(logTimer);
      logTimer = setTimeout(function () { logTimer = null; logQuery(r); }, LOG_DELAY);
    }

    function cancelNoMatch() {
      if (noMatchTimer) { clearTimeout(noMatchTimer); noMatchTimer = null; }
    }

    // Paints what's true right now. `immediate` skips the no-match hold — used on
    // open, on close, and when the timer itself fires.
    function paint(immediate) {
      paintClear();
      var r = view();
      scheduleLog(r);

      // Bad news waits for the typing to stop. Everything else is instant.
      // Already showing a no-match? Leave it up — re-delaying would blink it off.
      if (r.state === 'nomatch' && !immediate && !showingNoMatch) {
        cancelNoMatch();
        noMatchTimer = setTimeout(function () {
          noMatchTimer = null;
          paint(true);
        }, NOMATCH_DELAY);
        paintIndicator(r);   // the quiet note is honest either way, so it keeps up
        return;
      }
      cancelNoMatch();
      render(r);
    }

    // Reads the raw field, not the parsed terms — it's about what the rider can see
    // sitting there, not what the matcher made of it.
    function paintClear() {
      if (clear) clear.hidden = input.value.trim().length < CLEAR_MIN_CHARS;
    }

    function paintIndicator(r) {
      // Two or more characters typed AND more hits than we'll paint. Count, not
      // character count — 4 characters lands fine on a model (`yz65` 1, `mt09` 2) and
      // nowhere near it on a make (`husq` 114, `r120` 47). Count is the honest signal
      // because it's the same condition as "Enter won't get you anywhere". The
      // 2-character floor just stops it firing on `k`.
      if (!indicator) return;
      var show = r.chars >= INDICATOR_MIN_CHARS && r.count > threshold;
      if (show && !indicator.textContent) indicator.textContent = str('indicator');
      indicator.hidden = !show;
    }

    function render(r) {
      ensurePermanent();
      out.innerHTML = '';

      var wantsAsk = false;

      if (r.state === 'idle' || r.state === 'quiet') {
        // NOTHING. Both render an empty results area on purpose.
      } else if (r.state === 'resolved') {
        if (r.partialYear) out.appendChild(label(str('partial_year')));
        renderResolved(out, r.bike, r.years);
      } else if (r.state === 'recognised') {
        renderRecognised(out, r.bike, r.years);
        wantsAsk = true;
      } else if (r.state === 'pathway') {
        if (r.partialYear) out.appendChild(label(str('partial_year')));
        renderPathway(out, r.bikes, function (b) { picked = b; paint(true); },
                      r.yearMissed, r.years);
        // A word can be a bike AND a question. "contact" is a GASGAS trials model,
        // so the bike matcher took it outright and the how-to-reach-us answer never
        // appeared. Show both; the rider knows which one they meant.
        if (r.alsoAnswers && r.alsoAnswers.length) renderAnswers(out, r.alsoAnswers);
      } else if (r.state === 'keyword') {
        if (r.answers.length) renderAnswers(out, r.answers);
        if (r.shelves.length) renderShelves(out, r.shelves);
      } else {
        renderNoMatch(out);
        wantsAsk = true;
      }

      showingNoMatch = (r.state === 'nomatch');

      lastName = (r.state === 'recognised' && r.bike)
        ? bikeName(r.bike, r.years)
        : input.value.trim();

      if (ask) ask.hidden = captured ? false : !wantsAsk;
      paintIndicator(r);
    }

    // ENTER — keeps its first job and gains a second.
    //   one row showing  → open it (or the capture path, if it has no page)
    //   several showing  → nothing. The list is right there.
    //   above threshold  → nothing. The indicator already said why.
    //   no match         → the capture path, the only action on screen.
    // It never submits and never reloads, and it always drops the keyboard.
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      var r = view();
      if (r.state === 'resolved' && r.bike.url) {
        window.location.href = r.bike.url;
        return;
      }
      // Enter means "I've finished typing", so a pending no-match is due now —
      // otherwise the ask form would be hidden at the moment they asked for it.
      if (r.state === 'nomatch') paint(true);
      if (r.state === 'recognised' || r.state === 'nomatch') openAsk();
      dismissKeyboard(input);
    });

    input.addEventListener('input', function () {
      picked = null;
      resetAsk();   // new query = new ask; never carry a half-filled form across
      paint();
    });

    if (clear) {
      clear.addEventListener('click', function () {
        input.value = '';
        resetLog();
        picked = null;
        resetAsk();
        paint(true);      // straight back to empty, no half-second hold
        input.focus();    // they cleared it to type again, so put them back in it
      });
    }

    if (askOpen) {
      askOpen.addEventListener('click', function () {
        // Carries the bike TB recognised, not the fragment they typed. A rider who
        // typed "r1200" gets "BMW R1200GS 2004–2018" in the email.
        if (askBike) askBike.value = lastName || input.value.trim();
        askOpen.hidden = true;
        if (askWrap) {
          askWrap.hidden = false;
          // Do NOT autofocus on touch — the keyboard slams up and eats the form you
          // just tapped a button to look at. Desktop has no keyboard to fly.
          if (!isTouch()) {
            var first = askWrap.querySelector('input[type="email"]');
            if (first) first.focus();
          }
        }
      });
    }

    if (msgToggle && msg) {
      msgToggle.addEventListener('click', function () {
        var opening = msg.hidden;
        msg.hidden = !opening;
        msgToggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
        if (opening) {
          var t = msg.querySelector('textarea');
          if (t) t.focus();
        }
      });
    }

    // Send without leaving the visor.
    //
    // This does NOT fetch. It used to, and it never once worked: Shopify injects its
    // own spam-protection token into {% form 'contact' %}, an XHR post can't produce
    // one, so /contact answers 200, never sets contact_posted, and bins the message.
    // Every email that did arrive came from the NATIVE submit running underneath the
    // failed fetch — which is also what navigated the page, ate the success message
    // and poisoned the mobile back button.
    //
    // So let Shopify submit its own form, its own way, with its own token — just
    // aimed at a hidden frame instead of the tab. The rider never moves, the POST is
    // byte-for-byte the one Shopify expects, and the frame's landing URL tells us
    // whether it took.
    //
    // No JS at all? No target gets set, the form posts normally. Degrades, never dies.
    if (ask && !document.documentElement.hasAttribute('data-tb-ask-bound')) {
      document.documentElement.setAttribute('data-tb-ask-bound', '');

      var FRAME = 'tb-ask-sink';
      function sink() {
        var f = document.getElementById(FRAME);
        if (f) return f;
        f = document.createElement('iframe');
        f.id = FRAME;
        f.name = FRAME;
        f.setAttribute('aria-hidden', 'true');
        f.setAttribute('tabindex', '-1');
        f.style.cssText = 'position:absolute;width:0;height:0;border:0;left:-9999px;';
        document.body.appendChild(f);
        return f;
      }

      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.closest || !form.closest('[data-tb-ask-form]')) return;

        var scope  = form.closest('[data-tb-ask]') || document;
        var wrap   = scope.querySelector('[data-tb-ask-form]');
        var ok     = scope.querySelector('[data-tb-ask-ok]');
        var err    = scope.querySelector('[data-tb-ask-err]');
        var submit = scope.querySelector('[data-tb-ask-submit]');

        if (form.hasAttribute('data-tb-sending')) { e.preventDefault(); return; }
        form.setAttribute('data-tb-sending', '');
        if (err) err.hidden = true;

        var frame = sink();
        form.target = FRAME;          // the only thing we change about the submission
        var done = false;

        function settle(sent, why) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          frame.onload = null;
          form.removeAttribute('data-tb-sending');
          form.removeAttribute('target');
          if (submit) submit.disabled = false;
          if (sent) {
            if (wrap) wrap.hidden = true;
            if (ok) {
              ok.hidden = false;
              // Say it where they are looking — the form they were filling has just
              // gone, and silence in its place reads as the visor eating the message.
              if (ok.scrollIntoView) {
                try { ok.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
                catch (e2) { ok.scrollIntoView(); }
              }
            }
          } else {
            if (err) err.hidden = false;
            if (window.console) console.error('[TB Finder] ask', why);
          }
        }

        frame.onload = function () {
          // Same origin, so the landing URL is readable. If a frame policy blocks the
          // read we still know the POST left the browser — call it sent rather than
          // scare a rider whose message is already in the inbox.
          var landed = '';
          try { landed = frame.contentWindow.location.href; } catch (e2) { return settle(true, null); }
          if (!landed || landed === 'about:blank') return;
          settle(landed.indexOf('contact_posted=true') > -1 || landed.indexOf('/challenge') === -1,
                 'landed on ' + landed);
        };

        var timer = setTimeout(function () { settle(false, 'timed out'); }, 12000);

        // Button off AFTER the browser has taken the submission, never before.
        setTimeout(function () { if (submit) submit.disabled = true; }, 0);

        // NO preventDefault. That is the entire point.
      }, true);
    }

    function fail(e) {
      out.innerHTML = '';
      out.appendChild(el('p', 'tb-finder__nomatch-body',
        str('error') || 'Search is unavailable right now.'));
      if (window.console) console.error('[TB Finder]', e);
    }

    // Chrome first, bikes second. With the copy inline the shelves and the questions
    // are on screen before the index has landed, so the visor is never an empty frame.
    // ── capture ───────────────────────────────────────────────────────────
    // "Message to secure" pressed somewhere else on the site. No search happened,
    // so there is no bike name to borrow and no no-match to reveal the form — the
    // caller hands us its own context and we open straight into the form.
    //
    // The old build navigated to a capture page that was never made: blank on
    // desktop, 404 on mobile, and on mobile it poisoned the back button because
    // the rider had genuinely left the page. Nobody leaves the page now.
    root.addEventListener('tb:visor-capture', function (e) {
      if (!ask) return;
      var d = (e && e.detail) || {};
      cancelNoMatch();
      resetLog();
      showingNoMatch = false;
      picked = null;
      out.innerHTML = '';
      if (indicator) indicator.hidden = true;
      if (clear) clear.hidden = true;
      input.value = '';

      lastName = d.bike || '';
      if (askBike) askBike.value = d.bike || '';
      if (askPage) askPage.value = d.page || '';
      if (askItem) askItem.value = d.item || '';
      if (capNote) {
        capNote.textContent = d.note || '';
        capNote.hidden = !d.note;
      }

      captured = true;
      ask.hidden = false;
      if (askOpen) askOpen.hidden = true;
      if (askWrap) askWrap.hidden = false;
      if (askOk) askOk.hidden = true;
      if (askErr) askErr.hidden = true;

      // Same rule as the ask button: never autofocus on touch, the keyboard
      // swallows the form the rider just tapped to look at.
      if (!isTouch() && askWrap) {
        var f = askWrap.querySelector('input[type="email"]');
        if (f) setTimeout(function () { f.focus(); }, 40);
      }
    });

    root.addEventListener('tb:visor-open', function () {
      if (live) ensurePermanent();
      load(url).then(function () { paint(true); }).catch(fail);
    });

    root.addEventListener('tb:visor-close', function () {
      cancelNoMatch();
      resetLog();
      showingNoMatch = false;
      picked = null;
      lastName = '';
      input.value = '';
      out.innerHTML = '';
      if (indicator) indicator.hidden = true;
      if (clear) clear.hidden = true;
      resetAsk();
    });

    // Inline hero: no open event to wait for — load now and go live.
    if (inline) load(url).then(function () { paint(true); }).catch(fail);
  }

  function boot() {
    document.querySelectorAll('[data-tb-index]').forEach(function (root) {
      if (root.dataset.tbFinderReady) return;  // defer + DOMContentLoaded would double-bind
      root.dataset.tbFinderReady = '1';
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Shopify theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', boot);
})();
